import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { FONT, colors, radii } from '../../../theme';
import { S } from '../dashboardStyles';
import { api } from '../../../services/api';
import { displayName } from '../../../services/displayName';

/**
 * The warning, where something can be done about it.
 *
 * A message was refused because the account that sent it is now signed by a
 * different master key than the one this device pinned. That is what somebody
 * who lost every device and started over looks like — and it is also exactly
 * what a server impersonating them looks like, which is why the refusal is
 * automatic and the resolution is not.
 *
 * Until now the warning had nowhere to go. It was raised in the chat code,
 * stored, shown at the top of settings for ever, and could only be cleared
 * from inside that one conversation, by a button most people never reached.
 * Whoever read the banner on the settings screen could neither act on it nor
 * make it stop, so it became a red rectangle of unknown age — which is the
 * state in which a warning stops being a warning.
 *
 * The fingerprints to compare are on the card directly below this one. The
 * comparison is the one check in the system nobody's key can perform on a
 * person's behalf, so this card asks for it in words and then gets out of the
 * way.
 */
export default function AccountKeyChangedCard({ notice, myUserId, onAccept }) {
  const [name, setName] = useState('');
  const mine = notice?.userId != null && notice.userId === myUserId;

  useEffect(() => {
    if (!notice?.userId || mine) return;
    let live = true;
    api.getFriends()
      .then((friends) => {
        const who = (friends || []).find(f => f.id === notice.userId);
        if (live && who) setName(displayName(who));
      })
      .catch(() => { /* offline: the id below still identifies them */ });
    return () => { live = false; };
  }, [notice?.userId, mine]);

  if (!notice || notice.userId == null) return null;
  const who = mine ? 'вашего аккаунта' : (name ? `«${name}»` : `собеседника (id ${notice.userId})`);
  // Only ONE cause is answered by forgetting the pinned account key. Every
  // other refusal — a device missing from the signed list, keys that do not
  // match, a device never confirmed — is somebody else's to fix on their own
  // phone, and erasing the pin here would drop a check that never failed.
  const canAccept = !!notice.masterChanged;
  // When, and from which device — a warning without them is a claim about an
  // unknown moment, which is how the last one became impossible to judge.
  const when = notice.at ? new Date(notice.at).toLocaleString('ru-RU') : null;

  return (
    <div style={{
      ...S.settingsCard, padding: 16, marginBottom: 16,
      display: 'flex', flexDirection: 'column', gap: 12,
      background: 'rgba(200,40,40,0.10)',
    }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <AlertTriangle size={18} color={colors.gray900} style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          <div style={{ fontFamily: FONT, fontSize: 15, fontWeight: 600, color: colors.gray900 }}>
            {canAccept ? `Сменился ключ ${who}` : `Сообщение отклонено — ${mine ? 'ваш аккаунт' : who}`}
          </div>
          <div style={{ fontFamily: FONT, fontSize: 13, color: colors.gray600, marginTop: 4, lineHeight: 1.45 }}>
            {canAccept ? (
              <>
                Сообщение было отклонено: аккаунт подписан мастер-ключом, которого это устройство
                раньше не видело. Так выглядит человек, потерявший все устройства и начавший
                аккаунт заново — и так же выглядит подмена со стороны сервера. Разницу видно
                только по отпечатку, названному вслух.
              </>
            ) : (
              <>
                {notice.reason || 'подпись не сошлась с тем, что известно об этом устройстве'}.
                {' '}Мастер-ключ аккаунта при этом не менялся, поэтому принимать новый ключ
                здесь нечего: устройство должен подтвердить сам собеседник у себя — тогда оно
                попадёт в подписанный список, и сообщения пойдут.
              </>
            )}
          </div>
          {(when || notice.deviceId) && (
            <div style={{ fontFamily: FONT, fontSize: 12, color: colors.gray500, marginTop: 6 }}>
              {when}{when && notice.deviceId ? ' · ' : ''}{notice.deviceId ? `устройство ${String(notice.deviceId).slice(0, 8)}` : ''}
            </div>
          )}
        </div>
      </div>
      {canAccept && <button
        onClick={() => {
          if (window.confirm(`Принять новый ключ ${who}?\n\nСоглашайтесь, только если сверили отпечаток голосом.`)) {
            onAccept(notice.userId);
          }
        }}
        style={{
          border: `1px solid ${colors.gray300}`, background: 'transparent', color: colors.gray900,
          borderRadius: radii.sm, padding: '11px 16px', fontFamily: FONT, fontSize: 14,
          fontWeight: 500, cursor: 'pointer',
        }}>
        Сверил отпечаток — принять новый ключ
      </button>}
    </div>
  );
}
